import {
  Box,
  Chip,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import { ReferralProgram } from "../types/view.referral";
import { fallback } from "../utils/formatters";
import ButtonIcon from "../../../components/icons/PenPaperIcon";

interface Props {
  data: ReferralProgram;
}

const rowSx = {
  display: "grid",
  gridTemplateColumns: "160px 1fr",
  gap: 2,
  py: 1.4,
  borderBottom: "1px solid #E8ECF1",
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending: { bg: "#FFF7E6", color: "#D46B08" },
  active: { bg: "#E6F7FF", color: "#0958D9" },
  approved: { bg: "#F6FFED", color: "#389E0D" },
  rejected: { bg: "#FFF1F0", color: "#CF1322" },
};

export default function ReferralInfoCard({ data }: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const statusStyle = STATUS_COLORS[data.status] ?? {
    bg: "#F5F5F5",
    color: "#595959",
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "20px",
        p: 2.5,
        backgroundColor: colors.primary[900],
        height: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>
          Partner Information
        </Typography>
        <IconButton size="small">
          <ButtonIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Email — nested under referral_partner */}
      <Box sx={rowSx}>
        <Typography sx={{ color: "#98A3B3" }}>Email</Typography>
        <Typography sx={{ color: "#4E5969" }}>
          {fallback(data.referral_partner?.email)}
        </Typography>
      </Box>

      <Box sx={rowSx}>
        <Typography sx={{ color: "#98A3B3" }}>Phone Number</Typography>
        <Typography sx={{ color: "#4E5969" }}>
          {fallback(data.phone_number)}
        </Typography>
      </Box>

      <Box sx={rowSx}>
        <Typography sx={{ color: "#98A3B3" }}>Business Name</Typography>
        <Typography sx={{ color: "#4E5969" }}>
          {fallback(data.business_name)}
        </Typography>
      </Box>

      <Box sx={rowSx}>
        <Typography sx={{ color: "#98A3B3" }}>Address</Typography>
        <Typography sx={{ color: "#4E5969" }}>
          {fallback(data.business_address)}
        </Typography>
      </Box>

      <Box sx={rowSx}>
        <Typography sx={{ color: "#98A3B3" }}>Payment</Typography>
        <Typography sx={{ color: "#4E5969" }}>
          {fallback(data.payment_detail?.payment_method)}
        </Typography>
      </Box>

      <Box sx={rowSx}>
        <Typography sx={{ color: "#98A3B3" }}>Commission Rate</Typography>
        <Typography sx={{ color: "#4E5969" }}>
          {data.commission_rate != null ? `${data.commission_rate}%` : "N/A"}
        </Typography>
      </Box>

      <Box sx={{ ...rowSx, borderBottom: "none" }}>
        <Typography sx={{ color: "#98A3B3" }}>Status</Typography>
        <Box>
          <Chip
            label={data.status?.replace(/_/g, " ")}
            size="small"
            sx={{
              backgroundColor: statusStyle.bg,
              color: statusStyle.color,
              fontWeight: 600,
              fontSize: "0.75rem",
              textTransform: "capitalize",
              borderRadius: "6px",
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
}
